import {
  type Ga4ListInputProduct,
  type Ga4ListItem,
  buildGa4ListItems,
} from "~/hec/analytics/ga4-product-detail";
import { trackAbTastyEvent } from "~/lib/track-abtasty-event.client";
import { getDeviceType, trackSegment } from "~/segment/utils";

import { pushEcommerceEvent } from "./gtm-sink";

/**
 * Multi-sink event layer — product-tile-click events.
 *
 * A tile click fires TWO container events (dual shape), per the production GTM
 * containers + SFRA ground truth:
 *  - `GA4_productClick` — GA4 `ecommerce.items[]` shape → the `GA4 - select_item`
 *    / product-click GA4 event tag. Reuses `buildGa4ListItems`; `index` is
 *    overridden to the clicked tile's 1-based position.
 *  - `productClick` — legacy UA `ecommerce.click.products[]` shape → the Heap
 *    `Category/Search Product Clicked` tag, which reads
 *    `ecommerce.click.products.0.{id,name,brand,category,price,position}`. SFRA
 *    `gtm/init.js:242-256` reuses the impression `item.d` (master `id`, the list,
 *    + `position`).
 *
 * `id` is the MASTER product id; `variant` the SKU (null on non-variants).
 *
 * Design: docs/analytics-segment-first-event-layer.md
 */

export type Ga4ProductClickEvent = {
  event: "GA4_productClick";
  ecommerce: {
    item_list_id: string;
    item_list_name: string;
    items: Ga4ListItem[];
  };
};

export type UaProductClickEvent = {
  event: "productClick";
  ecommerce: {
    click: {
      actionField: { list: string };
      products: Array<{
        id: string;
        name: string | undefined;
        brand: string | undefined;
        category: string;
        price: number | undefined;
        position: number;
        variant: string | null;
        list: string;
      }>;
    };
  };
};

/** Build both product-click events from a normalized list-input product. */
export const buildProductClickEvents = (
  product: Ga4ListInputProduct,
  listId: string,
  listName: string,
  index: number,
): { ga4: Ga4ProductClickEvent; ua: UaProductClickEvent } => {
  const [ga4Item] = buildGa4ListItems([product], listId, listName);
  const list = listName || listId;
  return {
    ga4: {
      event: "GA4_productClick",
      ecommerce: {
        item_list_id: listId,
        item_list_name: listName,
        // Override the builder's array-position index with the clicked tile's
        // real 1-based position in the list.
        items: [{ ...ga4Item, index }],
      },
    },
    ua: {
      event: "productClick",
      ecommerce: {
        click: {
          actionField: { list },
          products: [
            {
              id: product.id,
              name: product.name,
              brand: product.brand,
              category: product.category ?? "",
              price: product.price,
              position: index,
              variant: product.variant ?? null,
              list,
            },
          ],
        },
      },
    },
  };
};

/**
 * Emit a product-tile click to all sinks: the always-on Segment `Product Clicked`
 * event (Segment ecommerce spec) PLUS the two GTM dataLayer events
 * (`GA4_productClick` + legacy `productClick`), each behind its per-event fork.
 * No-ops when `listId` is absent (a tile rendered without list context shouldn't
 * report a click). `siteId` decorates the Segment event's `sales_channel` to
 * match the <SegmentIdentify /> trailer.
 */
export const emitProductClick = (
  product: Ga4ListInputProduct,
  ctx: { listId: string; listName: string; index: number; siteId?: string },
): void => {
  if (!ctx.listId) return;
  const { ga4, ua } = buildProductClickEvents(
    product,
    ctx.listId,
    ctx.listName,
    ctx.index,
  );
  pushEcommerceEvent(ga4);
  pushEcommerceEvent(ua);

  // AB Tasty Universal Collect EVENT hit (SFCC-12079) — Vince's mapping names
  // this KPI `select_item`, which coincidentally matches this codebase's own
  // (unrelated) `select_item` GTM event in `gtm-select-item-event.ts` (CYP
  // cross-sell add). No technical collision — different systems — but don't
  // confuse the two when reading either file.
  trackAbTastyEvent({ ec: "Action Tracking", ea: "select_item" });

  // Segment sink (always on) — Segment ecommerce-spec `Product Clicked`.
  void trackSegment("Product Clicked", {
    product_id: product.id,
    sku: product.variant ?? product.id,
    name: product.name,
    brand: product.brand,
    price: product.price,
    category: product.category,
    position: ctx.index,
    list_id: ctx.listId,
    list_name: ctx.listName,
    device: getDeviceType(),
    ...(ctx.siteId ? { sales_channel: ctx.siteId } : {}),
  });
};
